import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { DxProgressBarModule } from 'devextreme-angular';
import { ValidationToastComponent } from 'src/app/components/validation-toast/validation-toast.component';
import { TemplateApplyJobModel, TemplateApplyStatusModel } from 'src/app/interfaces/template-apply.model';
import { AppGlobals } from 'src/app/app.globals';
import { TemplatesService } from 'src/app/services/templates.service';

@Component({
  selector: 'app-template-apply-banner',
  imports: [DxProgressBarModule, ValidationToastComponent],
  templateUrl: './template-apply-banner.component.html',
  styleUrl: './template-apply-banner.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class TemplateApplyBannerComponent implements OnInit, OnDestroy {
  status: TemplateApplyStatusModel | null = null;
  /** Failed jobs whose retry or dismiss is in flight or done: hidden, and a second click is ignored. */
  readonly busy = new Set<number>();
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  /** Only the newest load may write: a refresh racing a poll tick must not be overwritten by the older answer. */
  private loadSeq = 0;
  private destroyed = false;

  constructor(private templatesService: TemplatesService, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.refresh();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.stopPolling();
  }

  refresh(): void {
    this.load();
  }

  /** One request at a time: the next poll is armed only after the newest load has finished. */
  private async load(): Promise<void> {
    const seq = ++this.loadSeq;
    this.stopPolling();
    try {
      const result = await this.templatesService.getTemplateApplyStatus();
      if (this.destroyed || seq !== this.loadSeq) return;
      if (result?.hasError) {
        console.warn('GetTemplateApplyStatus failed', result.feedback);
      } else {
        this.status = result;
        this.cdr.markForCheck();
      }
    } catch {
      // ponytail: a failed poll is retried by the next tick; the banner keeps its last state
    }
    if (this.destroyed || seq !== this.loadSeq) return;
    const s = this.status;
    if (s && (!!s.running || s.queuedCount > 0 || s.recentlySucceeded?.length > 0)) {
      this.pollTimer = setTimeout(() => this.load(), 2000);
    }
  }

  private stopPolling(): void {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }

  get percent(): number {
    const r = this.status?.running;
    return r && r.totalProjects > 0 ? Math.round((r.doneProjects / r.totalProjects) * 100) : 0;
  }

  titles(job: TemplateApplyJobModel): string {
    return job.taskTitles.join(', ');
  }

  runningText(job: TemplateApplyJobModel): string {
    const titles = this.titles(job);
    // Phase null: claimed, no progress written yet - no step and no "0 of 0".
    const text = job.phase ? this.stepText(job, titles) : $localize`:@@templateApplyStarting:Changes to "${titles}:titles:" are being applied.`;
    const extras: string[] = [];
    if (job.attempt > 1) {
      extras.push($localize`:@@templateApplySecondAttempt:second attempt`);
    }
    const n = this.status?.queuedCount ?? 0;
    if (n > 0) {
      extras.push($localize`:@@templateApplyQueued:${n}:n: more changes waiting after this`);
    }
    return extras.length ? `${text} (${extras.join(', ')})` : text;
  }

  private stepText(job: TemplateApplyJobModel, titles: string): string {
    const rules = job.phase === 'Rules';
    const step = rules ? 2 : 1;
    const phase = rules
      ? $localize`:@@templateApplyPhaseRules:Apply rules`
      : $localize`:@@templateApplyPhasePropagation:Update projects`;
    const done = job.doneProjects;
    const total = job.totalProjects;
    return $localize`:@@templateApplyRunning:Changes to "${titles}:titles:" are being applied. Step ${step}:step: of 2: ${phase}:phase:, ${done}:done: of ${total}:total:`;
  }

  waitingText(): string {
    const n = this.status?.queuedCount ?? 0;
    return $localize`:@@templateApplyWaiting:Changes waiting to be applied (${n}:n:)`;
  }

  doneText(job: TemplateApplyJobModel): string {
    const titles = this.titles(job);
    return $localize`:@@templateApplyDone:Changes to "${titles}:titles:" have been applied.`;
  }

  failedText(job: TemplateApplyJobModel): string {
    const titles = this.titles(job);
    return $localize`:@@templateApplyFailed:Changes to "${titles}:titles:" could not be applied.`;
  }

  retryText = $localize`:@@templateApplyRetry:Apply again`;

  retry(job: TemplateApplyJobModel): Promise<void> {
    return this.call(job, () => this.templatesService.retryTemplateApplyJob(job.id));
  }

  dismiss(job: TemplateApplyJobModel): Promise<void> {
    return this.call(job, () => this.templatesService.dismissTemplateApplyJob(job.id));
  }

  /** On success the job stays hidden (the server no longer lists it); on failure its toast comes back. */
  private async call(job: TemplateApplyJobModel, request: () => Promise<any>): Promise<void> {
    if (this.busy.has(job.id)) return;
    this.busy.add(job.id);
    let ok = false;
    try {
      const result = await request();
      ok = !result?.hasError;
      if (!ok) {
        AppGlobals.ShowErrorMessage(result.feedback);
      }
    } catch (error) {
      AppGlobals.ShowErrorMessage(error);
    } finally {
      if (!ok) this.busy.delete(job.id);
      this.cdr.markForCheck();
      this.refresh();
    }
  }
}
